/**
 * Where a descent is aimed and lands: the surveyed site's centre, the hand-off
 * pin, or the body-fixed point under a body with no site, plus the pin's own
 * survey gate for the claim card and the quadrangle and sun that follow it.
 */

import * as THREE from 'three';

import type { AtlasLive, Site } from '../atlas';
import type { BodyReadersHandle } from './bodyReaders.ts';
import type { BodySceneHandle } from './bodyScene.ts';
import { bandEdges } from './camera.ts';
import type { DescentLoadHandle } from './descentLoad.ts';
import type { FocusRigHandle } from './focusRig.ts';
import type { InjectsHandle } from './injects.ts';
import { insideHexZ } from './plotField.ts';
import { isPyramidPlot } from './pyramidPlot.ts';
import type { SiteRegistryState } from './siteRegistry.ts';
import { urlFlag, TEST_SWITCHES } from '../urlFlags.ts';
import type { Quadrangle } from '../world/types';

export interface DescentAnchorDeps {
  site(): Site | undefined;
  readonly bodyReaders: BodyReadersHandle;
  readonly focusRig: FocusRigHandle;
  regionXZAt(lat: number, lon: number): { x: number; z: number } | null;
  quadFor(lat: number, lon: number): Quadrangle | undefined;
  renderOverlay(): void;
  readonly siteRegistry: SiteRegistryState;
  /** `cameraDirty` and `needsRender` are written from inside this seam. */
  readonly live: AtlasLive;
  readonly descentLoad: DescentLoadHandle;
  readonly bodyScene: BodySceneHandle;
  readonly injects: InjectsHandle;
}

export interface DescentAnchorHandle {
  regionAnchorLatLon(): { lat: number; lon: number } | null;
  standLatLon(): { lat: number; lon: number } | null;
  anchorLatLon(): { lat: number; lon: number } | null;
  displaySite(): Site | undefined;
  pinDescentAnchor(next: { body: string; lat: number; lon: number } | null): void;
  descentAnchor(): { lat: number; lon: number } | null;
  syncDescentAnchor(): void;
  availableQuad(): Quadrangle | undefined;
  groundUnsurveyed(): boolean;
  readonly _landV: THREE.Vector3;
  readonly _landQ: THREE.Quaternion;
  readonly anchorPin: { body: string; lat: number; lon: number } | null;
  landing: { body: string; lat: number; lon: number } | null;
  solarSite: { body: string; lat: number; lon: number } | null;
  standAt: { body: string; lat: number; lon: number } | null;
}

export function createDescentAnchor(deps: DescentAnchorDeps): DescentAnchorHandle {
  const { site, regionXZAt, quadFor, renderOverlay } = deps;
  const { ladderBody, descendable, corridorFor } = deps.bodyReaders;
  const { altitude, focusCam } = deps.focusRig;

  /**
   * The origin the loaded region's own metres are measured from.
   *
   * The site's own centre, from its baked meta once a plot is loaded and from
   * the registry before that. It used to be a Phoenix Lake constant in
   * Tharsis while the only baked ground was in Jezero, so the claim you landed
   * on was half a planet from the quad you descended through.
   *
   * Whatever the pin says: `regionXZAt` and everything that places the plot
   * mesh in the world are measured from the bake's own centre, and a hand-off
   * standing off the survey does not move the mesh it draws - what it changes
   * is what the ladder NAMES, which is `anchorLatLon` below.
   */
  function regionAnchorLatLon(): { lat: number; lon: number } | null {
    const s = site();
    if (s) return { lat: s.center_lat_deg, lon: s.center_lon_deg };
    // The registry row and the baked meta carry the same centre; the meta is
    // only reachable once a plot has loaded, so it is the fallback, not the
    // source. Filtered on the body the site list is on, the way
    // `honestyReading` filters the same field: an unfiltered fallback aims
    // Europa's descent at the last claim loaded on Mars and builds a hex ring
    // there.
    // A field built from the leaves is centred on the landing point itself, so
    // it carries its own anchor; `pyramidPlotBody` is the body it was built on,
    // which the slug lookup below has no row to find.
    const loadedFor = deps.live.plotField
      ? (isPyramidPlot(deps.live.plotField.meta)
        ? deps.descentLoad.state.pyramidPlotBody ?? undefined
        : deps.siteRegistry.SITES.find((st) => st.id === deps.live.plotField?.meta.slug)?.body)
      : undefined;
    return deps.live.plotField && loadedFor === ladderBody()
      ? { lat: deps.live.plotField.meta.center_lat_deg, lon: deps.live.plotField.meta.center_lon_deg }
      : null;
  }

  /**
   * Where the descent is AIMED, and so what the ladder names below the sphere:
   * the quadrangle it is open in, the cell the hex grid rings, and the point
   * `focusAim` reports. Every tier below the body sphere reads this one answer.
   *
   * The region centre, except under a hand-off pin that misses the loaded
   * site's own survey - `pinOffLoadedSite` below - where there is no surveyed
   * ground under the hull to name and this is null, the same answer a body
   * with no baked site gives. A dead-centre descent has no pin and reads what
   * it always did.
   */
  function anchorLatLon(): { lat: number; lon: number } | null {
    return pinOffLoadedSite() ? null : regionAnchorLatLon();
  }

  /**
   * The site whose name, quad and hex footprint the ladder actually shows.
   *
   * Not `site()` alone: away from a hand-off pin this IS the chip-selected
   * site, unchanged, but pinned it is that site only while the pin sits
   * inside its own baked hex grid - `pinOffLoadedSite` below - so a hand-off
   * pinned past the site's edge does not go on naming and ringing ground the
   * hull is not standing on. Every card and breadcrumb segment that used to
   * read `site()` for display reads this instead.
   */
  function displaySite(): Site | undefined {
    return pinOffLoadedSite() ? undefined : site();
  }

  /**
   * Where a descent lands on a body with no surveyed site, body-fixed degrees.
   *
   * Held per body and derived once, from the point the camera was already over
   * when the ladder first aimed at it, so the wheel lands where the player was
   * looking rather than at a hand-picked latitude.
   */
  let landing: { body: string; lat: number; lon: number } | null = null;
  const _landV = new THREE.Vector3();
  const _landQ = new THREE.Quaternion();

  /**
   * `?landingpole=1`: a siteless descent ignores the point it was opened on and
   * lands where the pose converges, which through ORBIT is the body's own pole.
   * `descent-dojo --ship --inject pole-landing` reaches it; nothing a player
   * presses does.
   */
  const LANDING_POLE_DEBUG = TEST_SWITCHES && urlFlag('landingpole');

  /**
   * `?poleanchor=1`: a descent that named no point takes the pose's own
   * answer, the pole, instead of the ground under the eye. `descent-dojo
   * --scroll-descent --inject pole-anchor` reaches it; nothing a player presses does.
   */
  const POLE_ANCHOR_DEBUG = TEST_SWITCHES && urlFlag('poleanchor');

  /**
   * `?floormemory=1`: the pitch lift leaves the floor as the pitch zooming back
   * out restores, instead of the one the player left. `descent-dojo --scroll-descent
   * --inject floor-memory` reaches it; nothing a player presses does.
   */
  const FLOOR_MEMORY_DEBUG = TEST_SWITCHES && urlFlag('floormemory');

  /**
   * The unit direction from a body's centre to the eye, in scene axes: through
   * ORBIT the eye looks at the centre, so this is the ground at the view centre.
   */
  const _eyeDir = new THREE.Vector3();
  function eyeDirection(centre: { x: number; y: number; z: number }): THREE.Vector3 | null {
    const eye = focusCam.eyeWorld();
    _eyeDir.set(eye.x - centre.x, eye.y - centre.y, eye.z - centre.z);
    return _eyeDir.lengthSq() === 0 ? null : _eyeDir.normalize();
  }

  /** The pitch the eye held when the ladder crossed the ORBIT edge, radians. */
  let pitchAtEdge = 0;
  /** True from a frame over the ORBIT edge until the first frame under it. */
  let crossedEdge = false;
  /** A descent ends looking at the ground from at least this far above its horizon, degrees. */
  const DESCENT_MIN_PITCH_DEG = 15;
  // 8 reaches the full lift by convergence 0.125; 4 still holds the landing bar, 1 does not.
  const PITCH_LIFT_RATE = 8;

  /**
   * Below the ORBIT edge the basis turns from the pole onto the anchor, and a
   * pitch under that basis's horizon (any view from the south) climbs over the
   * anchor and throws the altitude out; raise the pitch floor in step with the
   * aim, from the pitch held at the edge, so the eye never jumps.
   */
  function liftPitchWithAim(): void {
    // A dragged pitch follows nothing, so what the ceiling clamps under the
    // edge would stay clamped; the way back out hands the edge's pitch back.
    if (crossedEdge && focusCam.pitchMemory() === null) {
      focusCam.setOrientation(focusCam.state.yaw, focusCam.state.pitch, pitchAtEdge);
    }
    crossedEdge = false;
    const conv = focusCam.aim().convergence;
    const min = THREE.MathUtils.degToRad(DESCENT_MIN_PITCH_DEG);
    if (conv === null || pitchAtEdge >= min) return;
    const floor = pitchAtEdge + Math.min(1, conv * PITCH_LIFT_RATE) * (min - pitchAtEdge);
    if (focusCam.state.pitch < floor) focusCam.setOrientation(focusCam.state.yaw, floor, FLOOR_MEMORY_DEBUG ? undefined : pitchAtEdge);
  }

  /**
   * The ground the open descent was opened on, where it names some on `id`: a
   * LAND passes the point under the ship, and a body with no site lands there.
   *
   * Not the pose's own answer, which cannot be it: through ORBIT the pose is
   * built on the body's axis, so the point it is descending onto is the pole,
   * and Phobos's pole, with no season to tilt it, holds the sun on the horizon.
   */
  function namedLanding(id: string): { body: string; lat: number; lon: number } | null {
    const p = deps.descentLoad.state.descentFor;
    return !LANDING_POLE_DEBUG && p !== null && p.body === id ? p : null;
  }

  /**
   * A descent anchor pinned from outside: `descent-dojo --crossing --lat`, and
   * the sim's hand-off from a ship that has landed.
   *
   * The arm walks sites of its own choosing - the ones where the yaw reference
   * is at its worst - and every other path here derives the point from
   * wherever the pose happens to be looking. A grounded ship is the other
   * caller with a point of its own: the hull is where it is, and `anchorLatLon`
   * below would otherwise pull the ladder onto the body's surveyed site, which
   * is 9 degrees of great circle from where a Mars landing falls.
   */
  let anchorPin: { body: string; lat: number; lon: number } | null = null;

  /** Hold the descent on one body-fixed point, or hand the aim back to the app. */
  function pinDescentAnchor(next: { body: string; lat: number; lon: number } | null): void {
    anchorPin = next;
    if (anchorPin) focusCam.setAnchor(anchorPin.lat, anchorPin.lon);
    deps.live.cameraDirty = true;
    deps.live.needsRender = true;
    // The breadcrumb and the claim card read the pin synchronously, so a pin moved
    // mid hand-off has to be reflected now, not at the next incidental state change.
    renderOverlay();
  }

  /**
   * Where a SITELESS body's sun is read, body-fixed degrees.
   *
   * Not `landing`, which `syncDescentAnchor` re-derives every frame from a
   * scene direction the body turns under: its longitude slides at the spin
   * rate, so the sub-solar longitude and it move together, the hour angle
   * between them never changes and a clock scrub cannot move the sun. Titan is
   * the row that made that permanent - its imagery floor sits ABOVE its own
   * ORBIT edge, so the eye never reaches the band where the anchor stops
   * sliding, and solPhase held 0.9151 across a whole
   * 15.969-day solar day. This is the same ground held still for as long as
   * the eye is, and re-taken the moment the eye moves.
   */
  let solarSite: { body: string; lat: number; lon: number } | null = null;
  const _solarEyeDir = new THREE.Vector3();
  /** Hold the solar site while the eye's own direction moves less than this. */
  const SOLAR_SITE_HOLD_COS = Math.cos(THREE.MathUtils.degToRad(0.01));

  /**
   * The lat/lon the CAMERA descends onto: a site's centre, or a landing point.
   *
   * Not `anchorLatLon`, which is the surveyed site and is what the quadrangle
   * pick, the hex ring and the plot's tint read. This is the camera's alone,
   * because a body with no site row used to descend CENTRE-anchored and
   * `apply()` aims a centre anchor at the body's own centre: at the 1.7 m clamp
   * that is one texel of ground filling the frame, whatever the pyramid drew,
   * and a dragged pixel slides the eye 17.8 km over the ground on Earth instead
   * of turning the head. The label the manifest puts on these rows - ground to
   * SURFACE - promises the walkaround Mars gets, and the ladder below CLAIM was
   * written for a surface anchor throughout.
   */
  function descentAnchor(): { lat: number; lon: number } | null {
    if (anchorPin && anchorPin.body === ladderBody()) {
      return { lat: anchorPin.lat, lon: anchorPin.lon };
    }
    const a = anchorLatLon();
    if (a) return a;
    if (deps.injects.state.legacyStanding) return null;
    const id = ladderBody();
    // A body with no ground, or one that can only be focused at its centre (a
    // cloud-top corridor), has no surface point to land on at all.
    if (id === null || !descendable() || corridorFor(id) !== null) return null;
    const named = namedLanding(id);
    if (named) {
      landing = named;
      return { lat: named.lat, lon: named.lon };
    }
    if (landing?.body === id) return { lat: landing.lat, lon: landing.lon };
    const node = deps.bodyScene.state.bodyNode;
    if (!node || node.row.id !== id) return null;
    // `latLonOfHit`'s conversion against the body's SCENE-axes position rather
    // than its drawn one: `eyeWorld` is what the camera-relative frame
    // subtracts, so the two have to be read in the same frame.
    const dir = eyeDirection(node.world);
    if (!dir) return null;
    _landV.copy(dir).applyQuaternion(_landQ.copy(node.quaternion).invert());
    const lat = THREE.MathUtils.radToDeg(Math.asin(THREE.MathUtils.clamp(_landV.y, -1, 1)));
    let lon = THREE.MathUtils.radToDeg(Math.atan2(-_landV.z, _landV.x));
    if (lon < 0) lon += 360;
    landing = { body: id, lat, lon };
    return { lat, lon };
  }

  /**
   * How far a hand-off camera's own anchor sat from the surveyed site's
   * centre, in degrees of great circle - the gate this file used before this
   * fix, and the shape `--inject gate-from-centre` puts back. `CONTACT_BAR_DEG`
   * in `tools/preview/grounded-handoff-check.mjs` is the same closed bar for
   * the same geometry (0.05 deg, 3.0 km on Mars), unrelated to this one: that
   * bar is how near the CAMERA opened to the contact point, this one was how
   * near the contact point had to sit to the site to be treated as surveyed
   * at all - and Jezero's own baked hex grid reaches about 8.5 km out, so a
   * landing on the site's own outer hexes read unsurveyed under it.
   */
  const UNSURVEYED_PIN_BAR_DEG = 0.05;
  /**
   * `?gatefromcentre=1`: gate the claim on `UNSURVEYED_PIN_BAR_DEG` of the
   * site's centre again, rather than on the site's own baked hex footprint.
   * `grounded-handoff-check.mjs --inject gate-from-centre` reaches it; nothing
   * a player presses does.
   */
  const GATE_FROM_CENTRE_DEBUG = TEST_SWITCHES
    && new URLSearchParams(location.search).get('gatefromcentre') === '1';
  /**
   * `?claimonpin=1`: leave STAKE and RELINQUISH live on a pin that disagrees
   * with the surveyed site, the behaviour `groundUnsurveyed` below refuses.
   * `grounded-handoff-check.mjs --inject claim-on-pin` reaches it; nothing a
   * player presses does.
   */
  const CLAIM_ON_PIN_DEBUG = TEST_SWITCHES
    && new URLSearchParams(location.search).get('claimonpin') === '1';

  /**
   * Whether the pin lies inside the LOADED site's own baked hex grid - the
   * same per-cell test `subPlotAt` reads a click through, against
   * `plotField.meta.hexes` and the one `radius_m` every cell in it shares -
   * rather than within a fixed arc of its centre. This is the site `site()`
   * already fetched for the claim card, not a search of the body's other
   * rows: nothing here has fetched another site's footprint to test against,
   * so a pin that actually lands on one of those reads the same as a pin off
   * every site - unsurveyed, no name, no ring - rather than naming ground
   * whose hexes were never loaded.
   */
  function pinInsideSurvey(): boolean {
    return anchorPin !== null && insideSurvey(anchorPin.lat, anchorPin.lon);
  }
  function insideSurvey(lat: number, lon: number): boolean {
    if (!deps.live.plotField) return false;
    const m = deps.live.plotField.meta;
    // A bake with no hexes array is one cell at the field's own centre, same as PlotField.placements.
    const hexes = deps.live.plotField.placements;
    const xz = regionXZAt(lat, lon);
    if (!xz) return false;
    const r = m.hex.radius_m;
    return hexes.some((h) => insideHexZ(xz.x - h.x_m, xz.z - h.z_m, r));
  }

  /** The ground a jump named (`navigateTo`); honoured only while the focus camera is still anchored on it. */
  let standAt: { body: string; lat: number; lon: number } | null = null;

  /** The walkaround's pivot: a jump's own ground where the loaded survey covers it, else the region centre. */
  function standLatLon(): { lat: number; lon: number } | null {
    const s = standAt;
    if (s && s.body === ladderBody() && focusCam.state.latDeg === s.lat && focusCam.state.lonDeg === s.lon
      && insideSurvey(s.lat, s.lon)) {
      return { lat: s.lat, lon: s.lon };
    }
    return regionAnchorLatLon();
  }

  /**
   * An active hand-off pin that misses the loaded site's own survey - null
   * while the claim has not landed yet, which reads the same as a miss until
   * it has. `anchorLatLon`, `displaySite` and `groundUnsurveyed` all gate on
   * this one answer, so the breadcrumb, the ring and the claim card can never
   * disagree about whether the hull is standing on surveyed ground.
   */
  function pinOffLoadedSite(): boolean {
    return anchorPin !== null && anchorPin.body === ladderBody() && !pinInsideSurvey();
  }

  /**
   * True when the descent is pinned away from the surveyed ground under it -
   * the ground under the hull has no survey of its own, so the claim card
   * must not act on the nearest site's plot as if the player stood on it.
   */
  function groundUnsurveyed(): boolean {
    if (CLAIM_ON_PIN_DEBUG) return false;
    if (!anchorPin || anchorPin.body !== ladderBody()) return false;
    if (GATE_FROM_CENTRE_DEBUG) {
      const s = site();
      if (!s) return false;
      const r = Math.PI / 180;
      const p1 = anchorPin.lat * r;
      const p2 = s.center_lat_deg * r;
      const dp = (s.center_lat_deg - anchorPin.lat) * r;
      const dl = (s.center_lon_deg - anchorPin.lon) * r;
      const h = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
      const deg = (2 * Math.asin(Math.min(1, Math.sqrt(h)))) / r;
      return deg > UNSURVEYED_PIN_BAR_DEG;
    }
    return pinOffLoadedSite();
  }

  /**
   * Keep the landing point a property of THIS descent.
   *
   * Held above the ORBIT edge, where the aim is the body's own centre
   * (`anchorConvergence` is 0 there) and the anchor supplies nothing but the
   * local basis, and frozen the instant the camera crosses under it. Cached any
   * longer and a descent begun from the far side converges on the LAST
   * descent's ground, behind the limb - the owner's "the zoom target is stuck
   * at the first landing point". Re-derived rather than dropped, because
   * dropping it would put the pose back on a centre anchor and move the eye.
   *
   * `poseAnchorDir` and not the sub-camera point: at the ladder's own 0.35 rad
   * the eye is 44 degrees of arc from its anchor at that edge, so anchoring
   * under the camera would swing the eye by that arc every frame it is read.
   * The point this pose is already descending onto is on the near side by
   * construction, which is the whole of the fault.
   */
  function syncDescentAnchor(): void {
    // The hold is the other half of the one in `applyTierFraming`: a body with
    // no site row is re-anchored here instead, once a frame.
    if (deps.injects.state.legacyStickyAnchor || deps.injects.state.legacyStanding || deps.bodyScene.state.centreAnchorHold) return;
    const id = ladderBody();
    if (anchorPin && anchorPin.body === id) {
      focusCam.setAnchor(anchorPin.lat, anchorPin.lon);
      return;
    }
    if (id === null || anchorLatLon() !== null) return;
    if (!descendable() || corridorFor(id) !== null) return;
    const node = deps.bodyScene.state.bodyNode;
    if (!node || node.row.id !== id || focusCam.state.bodyId !== id) return;
    // Under the edge the anchor is ground already, and the sun is read over it.
    if (altitude() < bandEdges(node.row.radius_m).ORBIT) {
      solarSite = null;
      liftPitchWithAim();
      return;
    }
    pitchAtEdge = focusCam.state.pitch;
    crossedEdge = true;
    const named = namedLanding(id);
    if (named) {
      landing = named;
      solarSite = named;
      focusCam.setAnchor(named.lat, named.lon);
      return;
    }
    const dir = POLE_ANCHOR_DEBUG ? focusCam.poseAnchorDir() : eyeDirection(node.world);
    if (!dir) return;
    _landV.set(dir.x, dir.y, dir.z)
      .applyQuaternion(_landQ.copy(node.quaternion).invert())
      .normalize();
    const lat = THREE.MathUtils.radToDeg(Math.asin(THREE.MathUtils.clamp(_landV.y, -1, 1)));
    let lon = THREE.MathUtils.radToDeg(Math.atan2(-_landV.z, _landV.x));
    if (lon < 0) lon += 360;
    landing = { body: id, lat, lon };
    focusCam.setAnchor(lat, lon);
    // The same point, kept as a PLACE: taken off the eye's own direction, so a
    // clock that turns the body under a still eye leaves it where it was and
    // the sun crosses it.
    _landV.set(dir.x, dir.y, dir.z).normalize();
    if (solarSite === null || solarSite.body !== id
      || _solarEyeDir.dot(_landV) < SOLAR_SITE_HOLD_COS) {
      solarSite = { body: id, lat, lon };
      _solarEyeDir.copy(_landV);
    }
  }

  /** The quadrangle a descent is open in: the one holding the site's centre. */
  function availableQuad(): Quadrangle | undefined {
    const a = anchorLatLon();
    return a ? quadFor(a.lat, a.lon) : undefined;
  }

  return {
    regionAnchorLatLon,
    standLatLon,
    anchorLatLon,
    displaySite,
    pinDescentAnchor,
    descentAnchor,
    syncDescentAnchor,
    availableQuad,
    groundUnsurveyed,
    _landV,
    _landQ,
    get anchorPin() { return anchorPin; },
    get landing() { return landing; },
    set landing(v) { landing = v; },
    get solarSite() { return solarSite; },
    set solarSite(v) { solarSite = v; },
    get standAt() { return standAt; },
    set standAt(v) { standAt = v; },
  };
}
